<script setup>
import toast from '@/libs/cifra/toast';
import { isBlank } from '@/libs/strings';
import { useCifraClient } from 'cifra-api-js';
import {
  computed, inject, onMounted, ref, useTemplateRef, watch,
} from 'vue';
import { useTipoNotaActual } from './notificaciones';

const VARIABLES = {
  alumno: {
    variable: '%{alumno}',
    label: 'Alumno',
    descripcion: 'Nombre_completo',
  },
  asignatura: {
    variable: '%{asignatura}',
    label: 'Asignatura',
    descripcion: 'Asignatura',
  },
  nombre_item: {
    variable: '%{nombre_item}',
    label: 'nombre_item',
    descripcion: 'nombre_item',
  },
  descripcion_item: {
    variable: '%{descripcion_item}',
    label: 'descripcion_item',
    descripcion: 'descripcion_item',
  },
  calificacion_valor: {
    variable: '%{calificacion_valor}',
    label: 'valor_nota',
    descripcion: 'valor_nota',
  },
  calificacion_nombre: {
    variable: '%{calificacion_nombre}',
    label: 'nombre_nota',
    descripcion: 'nombre_nota',
  },
};

const I18n = inject('I18n');

const { cifraClient } = useCifraClient();

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  itemDiarioDefinicion: { type: Object, required: true },
  // Nos pasan los tipos de notas desde el padre para no estar cargándolos varias veces
  tiposNotas: { type: Array, default: () => [] },
});

const emits = defineEmits(['guardar', 'cancelar']);

const notificacion = ref({ ...props.modelValue });
const { tipoNotaActual } = useTipoNotaActual(ref(props.tiposNotas));

const tipoNotaConSelector = computed(() => {
  // Si no hay tipo de nota o no tiene opciones, texto libre
  if ((tipoNotaActual.value?.opciones || []).length === 0) return false;

  // Si es blanco es que no tiene valor y puede usar el tipo de nota actual
  if (notificacion.value.valor === '') return true;

  return !!tipoNotaActual.value.opciones.find((op) => op.valor === notificacion.value.valor);
});

// Watchers
watch(() => props.modelValue, (nuevasOpciones) => {
  notificacion.value = { ...nuevasOpciones };
});

const notificacionValida = computed(() => (
  !isBlank(notificacion.value.valor) && !isBlank(notificacion.value.mensaje)
));

function guardar() {
  if (!notificacionValida.value) {
    toast.error(I18n.t('Error_guardar_campos'));
  }
  emits('guardar', notificacion.value);
}

const summernoteInput = useTemplateRef('summernoteInput');

/* global $ */
onMounted(() => {
  $(summernoteInput.value).on('summernote.change', (_we, contents, _$editable) => {
    notificacion.value.mensaje = contents;
  });
});

function anadirVariable(clave) {
  const infoVariable = VARIABLES[clave];
  if (summernoteInput.value) {
    const summernoteInstance = $(summernoteInput.value);
    // Hay que hacer esto para insertar en la posición actual del cursor
    summernoteInstance.summernote('editor.saveRange');
    summernoteInstance.summernote('editor.restoreRange');
    summernoteInstance.summernote('editor.focus');
    summernoteInstance.summernote('editor.insertText', infoVariable.variable);
  }
  return infoVariable;
}

const previsualizacion = ref('');
async function previsualizar() {
  const { data } = await cifraClient.post('/calificaciones/items_diarios_notificaciones/previsualizacion', {
    item_diario_definicion_notificacion: {
      mensaje: notificacion.value.mensaje,
      nombre_item: props.itemDiarioDefinicion.nombre,
      descripcion_item: props.itemDiarioDefinicion.descripcion,
      calificacion_valor: notificacion.value.valor,
      calificacion_nombre: tipoNotaActual.value?.nombreParaValor(notificacion.value.valor) || 'Notable',
    },
  });
  previsualizacion.value = data;
}
</script>

<template>
  <form @submit.prevent="guardar">
    <div class="form-group">
      <label>{{ $t('valor') }}</label>
      <select
        v-if="tipoNotaConSelector"
        v-model="notificacion.valor"
        class="form-control"
      >
        <option value=""></option>
        <option
          v-for="opcion in tipoNotaActual.opciones"
          :key="opcion.valor"
          :value="opcion.valor"
        >
          {{ opcion.valor }} - {{ opcion.nombre }}
        </option>
      </select>
      <input v-else v-model="notificacion.valor" class="form-control" />
    </div>
    <ul class="nav nav-pills d-flex">
      <li class="nav-item active">
        <a href="#tab-mensaje" role="tab" data-toggle="tab" class="nav-link" aria-expanded="false">
          {{ $t('Mensaje') }}
        </a>
      </li>
      <li class="nav-item">
        <a
          href="#tab-previsualizacion"
          role="tab"
          data-toggle="tab"
          class="nav-link"
          aria-expanded="true"
          @click="previsualizar"
        >
          {{ $t('previsualizacion') }}
        </a>
      </li>
    </ul>
    <div class="tab-content m-t-xs">
      <div id="tab-mensaje" role="tabpanel" class="tab-pane fade in active">
        <div class="form-group">
          <textarea
            ref="summernoteInput"
            v-model="notificacion.mensaje"
            data-controller="summernote"
            data-height="150"
          ></textarea>
          <div class="m-t-xs m-b">
            <span
              v-for="(infoVariable, clave) in VARIABLES"
              :key="clave"
              v-tooltip="$t(infoVariable.descripcion)"
              class="label label-default m-r-xs clickable"
              @click="anadirVariable(clave)"
            >
              {{ $t(infoVariable.label) }}
            </span>
          </div>
        </div>
      </div>
      <div id="tab-previsualizacion" role="tabpanel" class="tab-pane fade">
        <div class="border rounded p-xs" v-html="previsualizacion"></div>
      </div>
    </div>

    <div class="form-actions m-t">
      <button type="button" class="btn btn-default" @click="$emit('cancelar')">
        {{ $t('Cancelar') }}
      </button>
      <button type="submit" class="btn btn-primary" :disabled="!notificacionValida">
        {{ $t('Aceptar') }}
      </button>
    </div>
  </form>
</template>

<style scoped lang="scss">
:deep(.note-editor.panel) {
  margin-bottom: 0;
}
</style>
